// src/components/auth/ProtectedRoute.tsx
import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { authService } from '@/services/authService';
import type { RootState } from '@/store';
import { logError } from '@/utils/logger';

interface ProtectedRouteProps {
    children: React.ReactNode;
}

export default function ProtectedRoute({ children }: ProtectedRouteProps) {
    const navigate = useNavigate();
    const { isAuthenticated, isLoading } = useSelector((state: RootState) => state.auth);

    useEffect(() => {
        const checkAuth = async () => {
            // Only check auth if we're not already authenticated
            if (!isAuthenticated) {
                // Check if we have a valid token
                if (!authService.isTokenValid()) {
                    // Store the current path before redirecting
                    const currentPath = window.location.pathname;
                    if (!currentPath.startsWith('/auth')) {
                        localStorage.setItem('lastVisitedPage', currentPath);
                    }
                    navigate('/auth/login');
                    return;
                }

                try {
                    // Try to refresh the token
                    await authService.refreshToken();
                } catch (error) {
                    const errorMessage = error instanceof Error ? error.message : 'An unknown error occurred';
                    logError(errorMessage);
                    // Store the current path before redirecting
                    const currentPath = window.location.pathname;
                    if (!currentPath.startsWith('/auth')) {
                        localStorage.setItem('lastVisitedPage', currentPath);
                    }
                    navigate('/auth/login');
                }
            }
        };

        checkAuth();
    }, [isAuthenticated, navigate]);

    if (isLoading) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-ps-blue-500"></div>
            </div>
        );
    }

    if (!isAuthenticated) {
        return null;
    }

    return <>{children}</>;
} 